import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import "./TextInput.scss";

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  hint?: string;
  icon?: ReactNode;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      id,
      label,
      error,
      hint,
      icon,
      className = "",
      required,
      disabled,
      ...inputProps
    },
    ref
  ) => {
    const inputId = id ?? inputProps.name;
    const errorId = inputId ? `${inputId}-error` : undefined;
    const hintId = inputId ? `${inputId}-hint` : undefined;

    const describedBy = [
      error ? errorId : undefined,
      !error && hint ? hintId : undefined,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={[
          "text-input",
          error ? "text-input--error" : "",
          disabled ? "text-input--disabled" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <label className="text-input__label" htmlFor={inputId}>
          {label}

          {required && (
            <span
              className="text-input__required"
              aria-hidden="true"
            >
              *
            </span>
          )}
        </label>

        <div className="text-input__control">
          {icon && (
            <span className="text-input__icon" aria-hidden="true">
              {icon}
            </span>
          )}

          <input
            {...inputProps}
            ref={ref}
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy || undefined}
            className={[
              "text-input__field",
              icon ? "text-input__field--with-icon" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          />
        </div>

        {error ? (
          <p
            id={errorId}
            className="text-input__message text-input__message--error"
            role="alert"
          >
            {error}
          </p>
        ) : hint ? (
          <p
            id={hintId}
            className="text-input__message"
          >
            {hint}
          </p>
        ) : null}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";